import { isAxiosError } from 'axios';
import { ErrorSeverity, MiscMetadata } from '@cbhq/error-vitals-web';

import { getErrorVitalsClient } from ':support/utils/error-vitals/init';

type RequiredErrorParams = {
  context: string;
  severity?: ErrorSeverity;
  isHandled?: boolean;
};

/**
 *
 * @param e unknown
 * @param metadata
 * context - location of the error, e.g. session_provider, create_case, get_account, attachment_upload.
 *
 * isHandled - isHandled means that the user is notified of an error and given a graceful way to retry
 * or go back OR the user never notices the error because it is mitigated by other logic, not that
 * it's just wrapped in a try/catch.
 *
 * severity - https://frontend.cbhq.net/errors/metadata#severity.
 * tldr:
 *
 * handled?
 * * no notification shown? warning
 * * notification shown but user can try again? error
 *
 * unhandled?
 *
 * * app still runs but chunks of it are missing or don't work? critical
 *
 * * app cannot be used until it restarts? crash
 *
 */
export function logError(
  e: unknown,
  { context, isHandled = true, severity = 'error' }: RequiredErrorParams,
  errorMetadata?: MiscMetadata,
) {
  const eIsError = e instanceof Error;
  const error = eIsError ? e : new Error(String(e));
  if (!eIsError) {
    error.cause = e;
  }

  const metadata = errorMetadata ?? {};

  if (isAxiosError(e)) {
    metadata.requestUrl = e.config?.url;
    metadata.message = e.response?.data?.message as string;
  }

  const client = getErrorVitalsClient();

  client.addBreadcrumb(error.message, metadata || {});

  client.logError(error, {
    context: !context || context === 'NOT_SET' ? 'cx_support_hub' : context,
    severity,
    isHandled,
    ...metadata,
  });
}
